import { serverInfo } from "../config/serverConfig";
import { checkResponse } from "../utils/apiError";


import { apiFetch } from "./apiFetch";

/**
 * Fetch saved insights for an account and user (user + account scoped).
 * @param {string} accountId
 * @param {string} [userId] - current user's _id; when provided, returns only that user's insights (plus legacy ones without user_id)
 * @returns {Promise<Array<{ id: string, name: string, createdAt: number, state: object }>>}
 */
export async function fetchSavedInsights(accountId, userId) {
  if (!accountId) return [];
  const resp = await apiFetch(serverInfo.serverUrl + "/getSavedInsights", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, user_id: userId || undefined }),
  });
  checkResponse(resp);
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Create a saved insight (stored under account + current user).
 * @param {string} accountId
 * @param {string} [userId] - current user's _id
 * @param {string} name
 * @param {object} state - { searchTerm, categoryFilter, projectFilter, timePhaseFilter, insightViewBy, viewType }
 * @returns {Promise<{ id: string, name: string, createdAt: number, state: object }>}
 */
export async function createSavedInsight(accountId, userId, name, state) {
  if (!accountId || !name?.trim()) throw new Error("account_id and name are required");
  const resp = await apiFetch(serverInfo.serverUrl + "/createSavedInsight", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId || undefined,
      name: name.trim(),
      state: state && typeof state === "object" ? state : {},
    }),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Failed to create saved insight");
  }
  return resp.json();
}

/**
 * Delete a saved insight (only if it belongs to this user or is legacy).
 * @param {string} accountId
 * @param {string} [userId] - current user's _id
 * @param {string} id - saved insight _id
 */
export async function deleteSavedInsight(accountId, userId, id) {
  if (!accountId || !id) throw new Error("account_id and id are required");
  const resp = await apiFetch(serverInfo.serverUrl + "/deleteSavedInsight", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, user_id: userId || undefined, id }),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Failed to delete saved insight");
  }
}
